import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";

import { cn } from "../../ui";
import type { ModuleItem, StatsRecord } from "./types";

type Props = {
  module: ModuleItem;
  stats: StatsRecord | null | undefined;
  onClick?: (m: ModuleItem) => void;
};

const formatCount = (raw: number | null | undefined) => {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return "—";
  return raw.toLocaleString();
};

const StatCard: React.FC<Props> = ({ module, stats, onClick }) => {
  const value = stats ? stats[module.statKey] : undefined;
  const display = useMemo(() => formatCount(value as number), [value]);
  const isEmpty = display === "—";

  return (
    <Link
      to={module.route}
      onClick={() => onClick?.(module)}
      className={cn(
        "group relative flex min-h-[140px] flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 transition-all",
        "hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_10px_30px_-12px_rgba(0,145,145,0.25)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
      )}
    >
      {/* Hover accent strip — single brand colour */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-brand transition-transform duration-300 ease-out group-hover:scale-x-100"
      />

      <div className="flex items-start justify-between gap-3">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/15 transition-colors group-hover:bg-brand group-hover:text-white group-hover:ring-brand"
        >
          {module.icon}
        </span>
        <span
          aria-hidden
          className="inline-flex h-7 w-7 translate-y-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 opacity-0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:border-brand/30 group-hover:bg-brand/10 group-hover:text-brand group-hover:opacity-100"
        >
          <HiArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>

      <div className="mt-auto min-w-0">
        <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
          {module.name}
        </p>
        <p
          className={cn(
            "mt-1 truncate text-[28px] font-semibold leading-none tracking-tight tabular-nums",
            isEmpty ? "text-gray-300" : "text-gray-900",
          )}
        >
          {display}
        </p>
      </div>
    </Link>
  );
};

export default StatCard;
